import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, DocumentTextIcon, ClockIcon, LanguageIcon, StarIcon } from '@heroicons/react/24/outline';
import { Transcript, transcriptionService } from '../services/transcription';
import toast from 'react-hot-toast';

interface TranscriptModalProps {
  appointmentId: string;
  onClose: () => void;
}

export const TranscriptModal: React.FC<TranscriptModalProps> = ({
  appointmentId,
  onClose
}) => {
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'content' | 'segments' | 'insights' | 'metadata'>('content');

  const fetchTranscript = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transcriptionService.getTranscriptByAppointment(appointmentId);
      setTranscript(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load transcript';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchTranscript();
  }, [fetchTranscript]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatConfidence = (confidence: number): string => {
    return `${Math.round(confidence * 100)}%`;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-[90%] max-w-6xl shadow-lg rounded-md bg-white min-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Raw Transcript Data
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={fetchTranscript}
              className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        )}

        {transcript && (
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'content', name: 'Raw Content', icon: DocumentTextIcon },
                  { id: 'segments', name: 'Segments', icon: ClockIcon },
                  { id: 'insights', name: 'Clinical Insights', icon: StarIcon },
                  { id: 'metadata', name: 'Metadata', icon: LanguageIcon }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <tab.icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
              {activeTab === 'content' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Raw Transcript Text</h4>
                    <div className="bg-white p-4 rounded border font-mono text-sm max-h-96 overflow-y-auto">
                      {transcript.content || 'No content available'}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'segments' && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    Time-stamped Segments ({transcript.segments?.length || 0})
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {transcript.segments?.map((segment, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">
                            {formatTime(segment.start)} - {formatTime(segment.end)}
                          </span>
                          <span className="text-xs text-gray-500">
                            Confidence: {formatConfidence(segment.confidence)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900">{segment.text}</p>
                      </div>
                    )) || (
                      <p className="text-gray-500 text-center py-8">No segments available</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'insights' && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">Clinical Insights</h4>
                  {transcript.clinicalInsights ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-red-50 p-4 rounded-md">
                        <h5 className="text-sm font-medium text-red-900 mb-2">Symptoms</h5>
                        <ul className="text-sm text-red-700 space-y-1">
                          {transcript.clinicalInsights.symptoms?.map((symptom, index) => (
                            <li key={index}>• {symptom}</li>
                          )) || <li className="text-gray-500">None detected</li>}
                        </ul>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-md">
                        <h5 className="text-sm font-medium text-blue-900 mb-2">Medications</h5>
                        <ul className="text-sm text-blue-700 space-y-1">
                          {transcript.clinicalInsights.medications?.map((med, index) => (
                            <li key={index}>• {med}</li>
                          )) || <li className="text-gray-500">None detected</li>}
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-md">
                        <h5 className="text-sm font-medium text-green-900 mb-2">Diagnoses</h5>
                        <ul className="text-sm text-green-700 space-y-1">
                          {transcript.clinicalInsights.diagnoses?.map((diagnosis, index) => (
                            <li key={index}>• {diagnosis}</li>
                          )) || <li className="text-gray-500">None detected</li>}
                        </ul>
                      </div>
                      
                      <div className="bg-purple-50 p-4 rounded-md">
                        <h5 className="text-sm font-medium text-purple-900 mb-2">Procedures</h5>
                        <ul className="text-sm text-purple-700 space-y-1">
                          {transcript.clinicalInsights.procedures?.map((procedure, index) => (
                            <li key={index}>• {procedure}</li>
                          )) || <li className="text-gray-500">None detected</li>}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No clinical insights available</p>
                  )}
                  
                  {transcript.clinicalInsights?.followUpRequired && (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                      <h5 className="text-sm font-medium text-yellow-900 mb-2">Follow-up Required</h5>
                      {transcript.clinicalInsights.followUpNotes && (
                        <p className="text-sm text-yellow-700">{transcript.clinicalInsights.followUpNotes}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'metadata' && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">Transcript Metadata</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Model</dt>
                        <dd className="mt-1 text-sm text-gray-900">{transcript.metadata.model}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Language</dt>
                        <dd className="mt-1 text-sm text-gray-900">{transcript.metadata.language}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Confidence</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formatConfidence(transcript.metadata.confidence)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formatDuration(transcript.metadata.duration)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Word Count</dt>
                        <dd className="mt-1 text-sm text-gray-900">{transcript.metadata.wordCount}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Time</dt>
                        <dd className="mt-1 text-sm text-gray-900">{transcript.metadata.processingTime}ms</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Version</dt>
                        <dd className="mt-1 text-sm text-gray-900">{transcript.version}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</dt>
                        <dd className="mt-1 text-sm text-gray-900">{new Date(transcript.createdAt).toLocaleString()}</dd>
                      </div>
                    </dl>
                  </div>

                  {transcript.qualityMetrics && (
                    <div className="bg-indigo-50 p-4 rounded-md">
                      <h5 className="text-sm font-medium text-indigo-900 mb-3">Quality Metrics</h5>
                      <dl className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-xs font-medium text-indigo-700">Overall Confidence</dt>
                          <dd className="mt-1 text-sm text-indigo-900">{formatConfidence(transcript.qualityMetrics.overallConfidence)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-indigo-700">Medical Accuracy</dt>
                          <dd className="mt-1 text-sm text-indigo-900">{formatConfidence(transcript.qualityMetrics.medicalTermAccuracy)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-indigo-700">Context Relevance</dt>
                          <dd className="mt-1 text-sm text-indigo-900">{formatConfidence(transcript.qualityMetrics.contextRelevance)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-indigo-700">Completeness</dt>
                          <dd className="mt-1 text-sm text-indigo-900">{formatConfidence(transcript.qualityMetrics.completeness)}</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};